import { FC, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquareTextLineIcon } from '@svg/MessageSquareTextLineIcon';
import { XLineIcon } from '@svg/XLineIcon';
import { useDebouncedValue } from '@src/hooks/useDebouncedValue';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ConversationFilter,
  useGetDriverChatUnreadCountQuery,
  useGetDriverConversationQuery,
} from '@store/rtk/driverChat';
import {
  closeDriverChatPanel,
  latchDriverChatLauncher,
  openPanelConversation,
  resetDriverChat,
  selectDriverChat,
  toggleDriverChatPanel,
} from '@store/slices/driverChat/driverChatSlice';
import { useDriverChatSocket } from '@component/DriverChat/hooks/useDriverChatSocket';
import { useDraggableFab } from '@component/DriverChat/hooks/useDraggableFab';
import { useDriverChatTabSync } from '@component/DriverChat/hooks/useDriverChatTabSync';
import { useDriverChatAccess } from '@component/DriverChat/hooks/useDriverChatAccess';
import {
  useDriverConversationCounts,
  useDriverConversations,
} from '@component/DriverChat/hooks/useDriverConversations';
import { useStartDriverChat } from '@component/DriverChat/hooks/useStartDriverChat';
import { useStartableSegments } from '@component/DriverChat/hooks/useStartableSegments';
import { ConversationList } from '@component/DriverChat/ConversationList/ConversationList';
import { ChatThread } from '@component/DriverChat/ChatThread/ChatThread';

const SEARCH_DEBOUNCE_MS = 500;
const PANEL_TITLE_ID = 'dc-panel-title';

/**
 * Dispatcher-only entry point: the floating button and its popup, which shows
 * the conversations list or a thread (also the one opened from "Message driver").
 */
export const DriverChatLauncher: FC = () => {
  const dispatch = useAppDispatch();
  const enabled = useDriverChatAccess();

  const {
    isPanelOpen,
    panelConversationId,
    panelSeed,
    panelSeedFailed,
    isLauncherLatched,
  } = useAppSelector(selectDriverChat);
  const [filter, setFilter] = useState<ConversationFilter>('MINE');
  const [search, setSearch] = useState('');
  const appliedSearch = useDebouncedValue(search.trim(), SEARCH_DEBOUNCE_MS);
  const { data: counts, refetch: refetchCounts } = useDriverConversationCounts(
    !enabled,
    appliedSearch
  );
  const { conversations, isLoading, isError, refetch } = useDriverConversations(
    filter,
    appliedSearch,
    !enabled || !isPanelOpen
  );
  const { data: openedConversation } = useGetDriverConversationQuery(
    enabled && panelConversationId != null ? panelConversationId : skipToken
  );
  const startable = useStartableSegments(enabled, conversations);
  const { start, isStarting } = useStartDriverChat();
  const { isDragging, fabStyle, panelStyle, guardClick, handlers } =
    useDraggableFab();
  const { data: unreadTotal = 0 } = useGetDriverChatUnreadCountQuery(
    undefined,
    {
      skip: !enabled,
    }
  );

  useDriverChatSocket(enabled);
  useDriverChatTabSync(enabled);

  // A driver has accepted a ride: from that moment the launcher shows on every
  // screen (until reload), even before the first message is sent.
  const isReadyToChat = startable.length > 0;
  useEffect(() => {
    if (enabled && isReadyToChat) dispatch(latchDriverChatLauncher());
  }, [enabled, isReadyToChat]);

  useEffect(
    () => () => {
      dispatch(resetDriverChat());
    },
    []
  );

  useEffect(() => {
    if (!isPanelOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dispatch(closeDriverChatPanel());
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPanelOpen]);

  if (!enabled) return null;

  // Visible on any screen once a conversation exists (today's closed ones too) or
  // once a driver has accepted a ride (latched). Otherwise there is nothing to show.
  const hasConversation =
    !!counts && (counts.mine > 0 || counts.active > 0 || counts.today > 0);
  if (!hasConversation && !isLauncherLatched && !isPanelOpen) {
    return null;
  }

  // "Message driver" opens the thread at once from its seed; the conversation id
  // arrives once the server has created it (until then the thread shows a skeleton).
  const panelConversation =
    panelSeed ??
    conversations.find((c) => c.conversationId === panelConversationId) ??
    (panelConversationId != null &&
    openedConversation?.conversationId === panelConversationId
      ? openedConversation
      : undefined);

  return createPortal(
    <div className="dc-scope">
      {isPanelOpen && (
        <div
          className="dc-panel"
          style={panelStyle}
          role="dialog"
          aria-labelledby={PANEL_TITLE_ID}
          data-test-id="driver-chat-panel"
        >
          {panelConversation ? (
            <ChatThread
              key={
                panelConversation.conversationId ??
                `seed-${panelConversation.segmentId}`
              }
              conversationId={panelConversation.conversationId ?? null}
              startFailed={panelSeedFailed}
              onRetryStart={() => panelSeed && start(panelSeed)}
              seed={panelConversation}
              mode="panel"
              titleId={PANEL_TITLE_ID}
              onBack={() => dispatch(openPanelConversation(null))}
              onClose={() => dispatch(closeDriverChatPanel())}
              dataTestId="driver-chat-panel"
            />
          ) : (
            <ConversationList
              conversations={conversations}
              counts={counts}
              filter={filter}
              onFilterChange={(next) => {
                setFilter(next);
                refetchCounts();
              }}
              search={search}
              onSearchChange={setSearch}
              appliedSearch={appliedSearch}
              isLoading={isLoading}
              isError={isError}
              onReload={refetch}
              startable={startable}
              onStart={start}
              isStarting={isStarting}
              activeConversationId={panelConversationId}
              onOpen={(conversationId) =>
                dispatch(openPanelConversation(conversationId))
              }
              onClose={() => dispatch(closeDriverChatPanel())}
              titleId={PANEL_TITLE_ID}
            />
          )}
        </div>
      )}

      <button
        type="button"
        className={`dc-fab ${isPanelOpen ? 'is-open' : ''} ${isDragging ? 'is-dragging' : ''}`}
        style={fabStyle}
        {...handlers}
        aria-label={isPanelOpen ? 'Close messages' : 'Open messages'}
        aria-expanded={isPanelOpen}
        onClick={guardClick(() => dispatch(toggleDriverChatPanel()))}
        data-test-id="driver-chat-launcher"
      >
        {isPanelOpen ? (
          <XLineIcon
            width={22}
            height={22}
            strokeWidth={2}
            aria-hidden="true"
          />
        ) : (
          <MessageSquareTextLineIcon
            width={24}
            height={24}
            strokeWidth={1.9}
            aria-hidden="true"
          />
        )}
        {!isPanelOpen && unreadTotal > 0 && (
          <span className="dc-badge dc-fab__badge">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>
    </div>,
    document.body
  );
};
